import { V2File } from '../v2Upload.d';
import { controllerAgnosticName } from './fileHelpers';

const OP_CLASSES: Array<[RegExp, number]> = [
  [/rough|clear|pocket|hog|waste/i, 0],
  [/finish|smooth|surfac/i, 1],
  [/detail|v-?carve|v-?bit|engrav|chamfer|bevel|text|inlay|drill/i, 2],
  [/cut-?\s?out|cutout|profile|perimeter|outline|tab/i, 3],
];

const UNCLASSED = 2;

function classOf(name: string): number {
  const found = OP_CLASSES.find(([re]) => re.test(name));
  return found ? found[1] : -1;
}

// ponytail: name-matching heuristic; the confirm checkbox is the correction path
export function fileCutRank(file: V2File): number {
  const named = (file.extracted?.operations ?? [])
    .map(classOf)
    .filter((rank) => rank >= 0);
  if (named.length) return Math.min(...named);
  // Carveco posts a mixed-case header the all-caps OP_NAME_RE never matches
  const fromName = classOf(file.name);
  return fromName >= 0 ? fromName : UNCLASSED;
}

/** Shallow before deep — maxCutDepth is a Z minimum, so it runs negative. */
function depthOf(file: V2File): number {
  return file.extracted?.maxCutDepth ?? 0;
}

export function byCutOrder(a: V2File, b: V2File): number {
  return (
    fileCutRank(a) - fileCutRank(b) ||
    depthOf(b) - depthOf(a) ||
    a.name.localeCompare(b.name)
  );
}

export function cutKey(file: V2File): string {
  const named = file.extracted?.operations?.[0];
  if (named) return named.trim().toLowerCase();
  // The controller sits in the name twice (Redline_… Redline.nc); leaving it
  // in means no two columns ever pair.
  return controllerAgnosticName(file.name);
}

/** Same cuts on both sides, one for one; the only case where ordering one by the other is safe. */
export function pairsOneForOne(keys: string[], reference: string[]): boolean {
  const sorted = (list: string[]) => [...list].sort().join('\n');
  return sorted(keys) === sorted(reference);
}

/**
 * The file a dragged card must land in front of to take the slot of the card at
 * `index`, or undefined for the end of the column. The dragged card is dropped
 * from the list first, because `reorderFile` splices it out before it inserts.
 */
export function dropBeforeIndex(
  ids: string[],
  dragId: string,
  index: number
): string | undefined {
  return ids.filter((id) => id !== dragId)[index];
}

/**
 * Which edge of slot `index` the insert bar belongs on, or null when that slot
 * is not the live drop target.
 *
 * An item dragged from above comes down INTO the slot, so the bar sits after
 * it; one dragged from below sits before. Getting this backwards is what makes
 * a downward drag look like it lands one slot short.
 */
export function insertEdge(
  ids: string[],
  dragId: string | null,
  hot: string | null,
  index: number
): 'before' | 'after' | null {
  const id = ids[index];
  if (!dragId || !id || dragId === id || hot !== id) return null;
  return ids.slice(0, index).includes(dragId) ? 'after' : 'before';
}

export function dropBeforeId(
  colFiles: V2File[],
  dragId: string,
  index: number
): string | undefined {
  return dropBeforeIndex(
    colFiles.map((f) => f.id),
    dragId,
    index
  );
}

// Columns are a filtered view of one array; rewrite only the slots they occupy
export function sortInPlace<T>(
  items: T[],
  pick: (item: T) => boolean,
  compare: (a: T, b: T) => number
): void {
  const slots = items.flatMap((item, i) => (pick(item) ? [i] : []));
  const sorted = slots.map((i) => items[i]).sort(compare);
  slots.forEach((slot, i) => {
    // eslint-disable-next-line no-param-reassign
    items[slot] = sorted[i];
  });
}
